// PREZENCE — Screenshot Viewer (Admin) Phase 6
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { todayLocal } from '../../utils/localDate';

export default function ScreenshotView() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected]   = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [date, setDate]           = useState(todayLocal());
  const [preview, setPreview]     = useState(null);
  const [blurMode, setBlurMode]   = useState(false);

  useEffect(() => {
    api.get('/users').then(r => {
      const emps = r.data.data || [];
      setEmployees(emps);
      if (emps.length > 0) setSelected(emps[0].id);
    }).catch(() => toast.error('Failed to load employees'));
  }, []);

  const fetchScreenshots = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await api.get(`/screenshots/employee/${selected}?date=${date}&limit=50`);
      setScreenshots(res.data.data || []);
    } catch { toast.error('Failed to load screenshots'); }
    finally { setLoading(false); }
  }, [selected, date]);

  useEffect(() => { fetchScreenshots(); }, [fetchScreenshots]);

  const flagScreenshot = async (id) => {
    const reason = window.prompt('Reason for flagging this screenshot:');
    if (!reason) return;
    try {
      await api.post(`/screenshots/${id}/flag`, { reason });
      toast.success('Screenshot flagged');
      fetchScreenshots();
    } catch { toast.error('Failed to flag screenshot'); }
  };

  const selectedEmp = employees.find(e => e.id === selected);
  const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 className="page-title">Screenshot Viewer</h1>
          <p className="page-sub">View and review employee screenshots captured by the desktop agent</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#64748b', cursor:'pointer' }}>
            <input type="checkbox" checked={blurMode} onChange={e=>setBlurMode(e.target.checked)} style={{ accentColor:'#63dcbc' }} />
            Blur screenshots
          </label>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        <select className="form-select" style={{ width:'auto', minWidth:200 }} value={selected} onChange={e=>setSelected(e.target.value)}>
          <option value="">Select Employee</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.full_name} — {e.department}</option>)}
        </select>
        <input className="form-input" type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ width:'auto' }} max={todayLocal()} />
        <button className="btn btn-secondary btn-sm" onClick={fetchScreenshots}>Refresh</button>
      </div>

      {/* Employee info banner */}
      {selectedEmp && (
        <div className="card" style={{ marginBottom:20, padding:'14px 20px', display:'flex', alignItems:'center', gap:14 }}>
          <div className="avatar avatar-md">{getInitials(selectedEmp.full_name)}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>{selectedEmp.full_name}</div>
            <div style={{ fontSize:12, color:'#475569' }}>{selectedEmp.department} · {selectedEmp.designation}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:22, fontWeight:800, color:'#63dcbc', fontFamily:"'JetBrains Mono'" }}>{screenshots.length}</div>
            <div style={{ fontSize:11, color:'#475569' }}>screenshots today</div>
          </div>
        </div>
      )}

      {/* Screenshots grid */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}>
          <div className="spinner" style={{ width:28, height:28 }} />
        </div>
      ) : screenshots.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🖥️</div>
            <div className="empty-state-text">No screenshots for this employee on {new Date(date).toLocaleDateString('en-IN',{day:'numeric',month:'long'})}<br/>Screenshots are taken every 5–10 minutes by the desktop agent.</div>
          </div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:14 }}>
          {screenshots.map(ss => (
            <div key={ss.id} style={{
              background:'#0d1520', border:`1px solid ${ss.is_flagged?'rgba(239,68,68,0.4)':'rgba(56,189,248,0.1)'}`,
              borderRadius:12, overflow:'hidden', cursor:'pointer',
              transition:'all 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor=ss.is_flagged?'rgba(239,68,68,0.6)':'rgba(99,220,188,0.3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor=ss.is_flagged?'rgba(239,68,68,0.4)':'rgba(56,189,248,0.1)'}
              onClick={() => setPreview(ss)}
            >
              <div style={{ position:'relative', aspectRatio:'16/9', background:'#080c14', overflow:'hidden' }}>
                <img
                  src={ss.image_url}
                  alt="Screenshot"
                  style={{
                    width:'100%', height:'100%', objectFit:'cover',
                    filter: blurMode ? 'blur(12px)' : 'none',
                    transition:'filter 0.3s',
                  }}
                  onError={e => { e.target.style.display='none'; }}
                />
                {ss.is_flagged && (
                  <div style={{ position:'absolute', top:8, right:8, background:'rgba(239,68,68,0.9)', borderRadius:6, padding:'2px 8px', fontSize:10, fontWeight:700, color:'#fff' }}>🚩 FLAGGED</div>
                )}
                <div style={{ position:'absolute', bottom:8, left:8, background:'rgba(0,0,0,0.7)', borderRadius:6, padding:'2px 8px', fontSize:10, color:'#94a3b8', fontFamily:"'JetBrains Mono'" }}>
                  {new Date(ss.captured_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                </div>
              </div>
              <div style={{ padding:'10px 12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'" }}>
                  {new Date(ss.captured_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                </span>
                <button onClick={e => { e.stopPropagation(); flagScreenshot(ss.id); }} style={{
                  background:'none', border:'none', cursor:'pointer', color:'#334155', fontSize:12,
                  padding:'2px 6px', borderRadius:4, transition:'color 0.15s',
                }}
                  onMouseEnter={e=>e.target.style.color='#ef4444'}
                  onMouseLeave={e=>e.target.style.color='#334155'}
                  title="Flag this screenshot"
                >🚩</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full preview modal */}
      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div style={{ maxWidth:900, width:'100%', background:'#0d1520', borderRadius:16, border:'1px solid rgba(56,189,248,0.15)', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(56,189,248,0.08)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#94a3b8', fontFamily:"'JetBrains Mono'" }}>
                {new Date(preview.captured_at).toLocaleString('en-IN',{day:'numeric',month:'long',hour:'2-digit',minute:'2-digit',second:'2-digit'})}
              </div>
              <button className="modal-close" onClick={() => setPreview(null)}>✕</button>
            </div>
            <img
              src={preview.image_url}
              alt="Screenshot full"
              style={{ width:'100%', display:'block' }}
              onError={e => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display:'none', padding:'40px 20px', textAlign:'center', color:'#64809a', fontSize:13 }}>
              Couldn't load this image. Try refreshing the page.
            </div>
            {preview.is_flagged && (
              <div style={{ padding:'10px 20px', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:12 }}>🚩 Flagged: {preview.flag_reason}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
